import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import useCan from '../hooks/useCan';
import queueService from '../services/queueService';
import DeleteQueueDialog from '../components/queues/DeleteQueueDialog';

// Attesa prima di chiedere il suggerimento: non una chiamata a ogni tasto premuto.
const DEBOUNCE_SUGGERIMENTO_MS = 300;

const MODULO_VUOTO = {
  code: '',
  description: '',
  priority: 0,
  capacity: 100,
  candidateGroup: ''
};

// Intero (>= minimo) da un valore di campo; i campi numerici tengono la stringa digitata.
const interoDa = (valore, minimo) => {
  const n = Number(valore);
  return String(valore).trim() !== '' && Number.isInteger(n) && n >= minimo;
};

const Queues = () => {
  const can = useCan();
  const canCreateQueue = can('queues.create');
  
  // Queues state
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Queue dialog state
  const [queueDialog, setQueueDialog] = useState(false);
  const [queueAction, setQueueAction] = useState('');
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueForm, setQueueForm] = useState(MODULO_VUOTO);
  const [dialogError, setDialogError] = useState(null);
  // Se l'utente ha scritto il candidate group a mano, il codice smette di suggerirlo.
  const [candidateGroupToccato, setCandidateGroupToccato] = useState(false);
  // null finche' la verifica non e' tornata: nel frattempo il campo resta chiuso.
  const [candidateGroupBloccato, setCandidateGroupBloccato] = useState(null);

  // Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load queues
  useEffect(() => {
    fetchQueues();
  }, []);
  
  // Fetch queues
  const fetchQueues = async () => {
    try {
      setLoading(true);
      const data = await queueService.getAll();
      setQueues(data);
    } catch (err) {
      console.error('Error fetching queues:', err);
      setError('Failed to load queues. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Open queue dialog
  const handleOpenQueueDialog = (action, queue = null) => {
    setQueueAction(action);
    setDialogError(null);
    setCandidateGroupBloccato(null);

    if (action === 'edit' && queue) {
      setSelectedQueue(queue);
      // In modifica il candidate group e' quello gia' scelto: nessun suggerimento.
      setCandidateGroupToccato(true);
      setQueueForm({
        code: queue.code ?? '',
        description: queue.description ?? '',
        priority: queue.priority ?? 0,
        capacity: queue.capacity ?? 1,
        candidateGroup: queue.candidateGroup ?? ''
      });
    } else {
      setSelectedQueue(null);
      setCandidateGroupToccato(false);
      setQueueForm(MODULO_VUOTO);
    }

    setQueueDialog(true);
  };

  // In modifica, chiede al backend se il candidate group e' ancora cambiabile.
  useEffect(() => {
    if (!queueDialog || queueAction !== 'edit' || !selectedQueue) return undefined;
    let annullato = false;
    queueService.candidateGroupBloccato(selectedQueue.id)
      .then(bloccato => { if (!annullato) setCandidateGroupBloccato(bloccato); })
      // Se la verifica fallisce il campo non si blocca a vuoto: l'ultima parola e' del
      // backend, che rifiuta con 409 il cambio su una coda che ha avuto attivita'.
      .catch(() => { if (!annullato) setCandidateGroupBloccato(false); });
    return () => { annullato = true; };
  }, [queueDialog, queueAction, selectedQueue]);

  // In creazione, il candidate group segue il codice finche' nessuno lo tocca a mano.
  useEffect(() => {
    if (!queueDialog || queueAction !== 'create' || candidateGroupToccato) return undefined;
    const code = queueForm.code.trim();
    if (!code) return undefined;
    let annullato = false;
    const timer = setTimeout(() => {
      queueService.suggerisciCandidateGroup(code)
        .then(suggerito => {
          if (!annullato && suggerito) {
            setQueueForm(prev => ({ ...prev, candidateGroup: suggerito }));
          }
        })
        // Il suggerimento e' una comodita': se manca, il campo resta da scrivere.
        .catch(() => {});
    }, DEBOUNCE_SUGGERIMENTO_MS);
    return () => {
      annullato = true;
      clearTimeout(timer);
    };
  }, [queueDialog, queueAction, candidateGroupToccato, queueForm.code]);

  const candidateGroupDisabilitato = queueAction === 'edit' && candidateGroupBloccato !== false;

  const formValido =
    queueForm.code.trim() !== '' &&
    queueForm.candidateGroup.trim() !== '' &&
    interoDa(queueForm.priority, 0) &&
    interoDa(queueForm.capacity, 1);
  
  // Close queue dialog
  const handleCloseQueueDialog = () => {
    setQueueDialog(false);
  };
  
  // Handle queue form change
  const handleQueueFormChange = (e) => {
    const { name, value } = e.target;
    setQueueForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCandidateGroupChange = (e) => {
    setCandidateGroupToccato(true);
    handleQueueFormChange(e);
  };

  // Submit queue form
  const handleSubmitQueueForm = async () => {
    try {
      setLoading(true);
      setDialogError(null);

      const dati = {
        code: queueForm.code.trim(),
        description: queueForm.description,
        priority: Number(queueForm.priority),
        capacity: Number(queueForm.capacity),
        candidateGroup: queueForm.candidateGroup.trim()
      };

      if (queueAction === 'create') {
        await queueService.create(dati);
      } else if (queueAction === 'edit' && selectedQueue) {
        await queueService.update(selectedQueue.id, dati);
      }

      handleCloseQueueDialog();
      fetchQueues();
    } catch (err) {
      console.error('Error submitting queue form:', err);
      // 400 e 409 portano nel messaggio il perche' (nome gia' preso, rinomina dopo che la
      // coda ha avuto attivita', carattere non ammesso): l'utente deve poterlo leggere.
      setDialogError(
        err?.response?.data?.message || 'Non è stato possibile salvare la coda. Riprova.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  // Open delete confirmation
  const handleConfirmDelete = (queue) => {
    setSelectedQueue(queue);
    setDeleteConfirmOpen(true);
  };
  
  // Delete queue
  const handleDeleteQueue = async () => {
    try {
      setLoading(true);
      
      if (selectedQueue) {
        await queueService.delete(selectedQueue.id);
        fetchQueues();
      }
      
      setDeleteConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting queue:', err);
      setError('Failed to delete queue. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter queues
  const filteredQueues = queues.filter(queue => {
    const matchesSearch =
      !searchQuery ||
      (queue.code && queue.code.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (queue.description && queue.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Code di lavoro
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per codice o descrizione..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {canCreateQueue && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenQueueDialog('create')}
                  >
                    Aggiungi
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          {loading && (!queues || queues.length === 0) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : !queues || queues.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No queues configured</Typography>
        </Paper>
      ) : (
        <TableContainer component={(props) => <Paper elevation={0} variant="outlined" {...props} />}
          sx={{ mt: 2 }}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell>Codice</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Priorità</TableCell>
                <TableCell>Capacità</TableCell>
                <TableCell align="center">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQueues.map((queue) => (
                <TableRow key={queue.id} hover sx={{ height: 48 }}>
                  <TableCell>
                    <Chip label={queue.code} color="primary"  variant="outlined" size="small" />
                  </TableCell>
                  <TableCell>{queue.description}</TableCell>
                  <TableCell>
                    <Chip
                      label={String(queue.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{queue.capacity}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      edge="end"
                      color="primary"
                      onClick={() => handleOpenQueueDialog('edit', queue)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleConfirmDelete(queue)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
        </Box>
      </Paper>
      
      
      
      {/* Queue Dialog */}
      <Dialog open={queueDialog} onClose={handleCloseQueueDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {queueAction === 'create' ? 'Nuova coda' : 'Modifica coda'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* L'errore sta nel dialogo: il 409 spiega un conflitto che l'utente risolve
                qui dentro, e il messaggio di pagina resterebbe dietro lo sfondo scuro. */}
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            <TextField
              fullWidth
              required
              label="Codice"
              name="code"
              value={queueForm.code}
              onChange={handleQueueFormChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Descrizione"
              name="description"
              value={queueForm.description}
              onChange={handleQueueFormChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              required
              label="Priorità"
              name="priority"
              type="number"
              value={queueForm.priority}
              onChange={handleQueueFormChange}
              InputProps={{ inputProps: { min: 0, step: 1 } }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              required
              label="Capacità"
              name="capacity"
              type="number"
              value={queueForm.capacity}
              onChange={handleQueueFormChange}
              InputProps={{ inputProps: { min: 1, step: 1 } }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              required
              label="Candidate group"
              name="candidateGroup"
              value={queueForm.candidateGroup}
              onChange={handleCandidateGroupChange}
              disabled={candidateGroupDisabilitato}
              helperText={
                candidateGroupDisabilitato && queueAction === 'edit' && candidateGroupBloccato
                  ? 'La coda ha già avuto attività: il candidate group non si può più cambiare.'
                  : 'Deve coincidere con il candidate group indicato negli step del processo.'
              }
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQueueDialog}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSubmitQueueForm}
            disabled={!formValido || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Eliminare una coda avverte chi la perde (piano "code sui ruoli", Task 16): il
          dialogo generico "Are you sure?" non diceva a nessuno quali ruoli avrebbero perso
          la coda, ne' se qualcuno sarebbe rimasto senza nessuna. */}
      <DeleteQueueDialog
        queueId={selectedQueue?.id}
        queueName={selectedQueue?.code}
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteQueue}
      />
    </Box>
  );
};

export default Queues; 